/* eslint-disable react/prop-types */
import * as React from 'react';
import { Portal } from 'react-portal';

import { motion } from 'framer-motion';
import { Link } from 'gatsby';
import styled from 'styled-components';

import { Typography } from 'components/DesignSystemV2/Typography';
import { useDolInfo } from 'shared/useDolInfo';
import VideoInline from 'shared/VideoThumbnailInline/VideoInline';
import { colorsV2 } from 'style/colors-v2';
import { shadows } from 'style/shadows';
import { getAssetLinkByHandle } from 'utils/getAssetLink';
import { useWindowSize } from 'utils/hooks/useWindowSize';
import { getClassGroupName } from 'utils';
import { useMounted } from '../../../utils/hooks/useMounted';
import { usePromotionData } from '../../SchedulePage/FixedTopBarScholarship/usePromotionData';
import { useGCMSLevel } from './useGCMSLevel';
import DolInfo from '../../DolInfo';
import { GCMSPromotionInfo } from './PromotionInfo';
import Button from '../../../components/Button';
import { useStickyCourseContext } from './StickyCourseContext';
import { fromScreen } from '../../../utils/media-query/responsive.util';
import { CourseInfo, CourseInfoJunior } from './CourseInfo';
import 'react-responsive-modal/styles.css';

const DetailScholarshipsContext = React.createContext(null);

export const GGSheetScholarshipsData = props => {
  const dolInfo = useDolInfo();
  const fromSheets = usePromotionData({ filterBy: 'offline' });

  const promotions = fromSheets.map(sheet => {
    const { value, remainQuantity, startDate, endDate, courseName } = sheet;
    return {
      discount: value,
      scholarships: remainQuantity,
      startDate,
      endDate,
      courseName
    };
  });

  const { level, syllabus, rating, children, showVideo, showCommitment } =
    props;

  return (
    <DetailScholarshipsContext.Provider
      value={{
        level,
        syllabus,
        promotions,
        dolInfo,
        rating,
        showVideo,
        showCommitment
      }}
    >
      {children}
    </DetailScholarshipsContext.Provider>
  );
};

export const GGSheetScholarships = props => {
  return (
    <GGSheetScholarshipsData {...props}>
      <DetailScholarships />
    </GGSheetScholarshipsData>
  );
};

const InvisibleWrapper = styled.div`
  display: none;
`;

export const DetailScholarships = props => {
  const [mounted, setMounted] = React.useState(false);
  const [, updateState] = React.useState();
  const forceUpdate = React.useCallback(() => updateState({}), []);
  const { isEnabled } = useStickyCourseContext();
  useWindowSize(forceUpdate);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted || isEnabled) {
    return (
      <InvisibleWrapper>
        <DetailScholarshipsBase {...props} />
      </InvisibleWrapper>
    );
  }

  // if (mounted && window.innerWidth < 375)
  //   return <DetailScholarshipsBase {...props} />;

  return (
    <>
      <Portal
        node={
          document &&
          document.getElementById('course-landing-detail-header-slot')
        }
      >
        <DetailScholarshipsBase {...props} />
      </Portal>
    </>
  );
};

const CTASectionWrapper = styled.div`
  .btn-cta {
    margin-top: 16px;

    button {
      font-family: 'Inter';
      width: 100%;
      border-radius: 8px;
      background-color: ${colorsV2.primary100};
      font-weight: 600;
    }

    &.outline {
      button {
        background-color: ${colorsV2.white100};
        border: 1px solid ${colorsV2.primary100};
        color: ${colorsV2.primary100};
      }
    }
  }
`;
const CTASection = () => {
  return (
    <CTASectionWrapper className="cta-section">
      <DolInfo>
        {({ dolInfo }) => (
          <div className="btn-cta fb-message">
            <Link
              target="_blank"
              href={dolInfo.messenger.href}
              rel="noreferrer noopener"
            >
              <Button>
                <Typography variant="semi-bold/14-24" color={colorsV2.white100}>
                  Nhắn DOL qua FB
                </Typography>
              </Button>
            </Link>
          </div>
        )}
      </DolInfo>
      {/* <div className="btn-cta outline cta-contact">
        <ButtonRegister />
      </div> */}
    </CTASectionWrapper>
  );
};
const CustomContainer = styled.div``;

export const DetailScholarship = ({
  showVideo = true,
  showCommitment = true
}) => {
  const { level = {}, promotions } = React.useContext(
    DetailScholarshipsContext
  );
  const levelData = useGCMSLevel();
  const totalHours = levelData.levelToHour[level.level] || '54';
  const mounted = useMounted();
  const isJunior = level.classGroup.includes('Junior');

  const promotionByClassGroup = promotions.find(promotion =>
    promotion.courseName.includes(getClassGroupName(level.classGroup))
  );
  return (
    <Container>
      {level.levelVideo && showVideo ? (
        <VideoBorder>
          <LevelVideo
            video={getAssetLinkByHandle({ handle: level.levelVideo })}
            poster={level.levelVideoThumbnailHandle}
            alt={level.levelDisplay}
            onContextMenu={e => e.preventDefault()}
            controlsList="nodownload"
          />
        </VideoBorder>
      ) : null}

      <Body>
        {promotionByClassGroup && (
          <>
            <div className="promotion">
              <GCMSPromotionInfo
                classGroup={level.classGroup}
                promotionByClassGroup={promotionByClassGroup}
              />
            </div>
            <CTASection />
          </>
        )}

        {isJunior ? (
          <CourseInfoJunior totalHours={totalHours} mounted={mounted} />
        ) : (
          <CourseInfo
            level={level}
            showCommitment={showCommitment}
            totalHours={totalHours}
            mounted={mounted}
          />
        )}
        {!promotionByClassGroup && <CTASection />}
      </Body>
    </Container>
  );
};
export const DetailScholarshipsBase = () => {
  const { showVideo, showCommitment } = React.useContext(
    DetailScholarshipsContext
  );

  return (
    <Section selfish id="course-landing-detail-promotion-2">
      <CustomContainer>
        <DetailScholarship
          showVideo={showVideo}
          showCommitment={showCommitment}
        />
      </CustomContainer>
    </Section>
  );
};

const LevelVideo = styled(VideoInline)`
  video {
    border-radius: 8px;
  }
  > div {
    margin-bottom: 0;
  }
  ${fromScreen(1144)} {
    .thumbnail {
      max-height: 186px;
    }
  }
`;

const Section = styled.div`
  ${fromScreen(1144)} {
    overflow: visible;
    ${CustomContainer} {
      width: auto;
    }

    height: 0;
    padding: 0;

    .separator {
      display: none;
    }

    .container {
      position: static;
    }
  }
`;

const VideoBorder = styled.div`
  border-radius: 8px;
  font-size: 8px;
  overflow: hidden;
`;

const Body = styled.div`
  .promotion {
    display: none;
  }
`;

const Container = styled(motion.div)`
  width: 100%;
  .course-info {
    display: none;
  }

  ${fromScreen(1144)} {
    background-color: ${colorsV2.white100};
    box-shadow: ${shadows.zIndex3};
    border-radius: 8px;
    padding: 4px;

    .course-info {
      display: block;
    }
    ${Body} {
      padding: 16px;
      .promotion {
        display: block;
      }
    }
  }
`;

DetailScholarships.propTypes = {};
